<template>
    <div class="manage-client-documents mb-5">
        <div class="d-flex justify-content-between align-items-center">
            <h3 class="mb-0">Client Tasks</h3>
            <button class="btn btn-primary" @click="openTaskModal">
                <img class="icon" src="/img/icons/avid-icons/plus.svg" alt=""> Add Task
            </button>
        </div>
        <div class="row">
            <div class="col-12 g-3">
                <div class="card">
                    <div class="card-body">
                        <div class="table-responsive">
                            <table class="table table-without-pagination">
                                <thead>
                                    <tr>
                                        <th>Completed</th>
                                        <th>Name</th>
                                        <th>Description</th>
                                        <th>URL</th>
                                        <th>Due At</th>
                                        <th>Hubspot Field to update</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="task in lockstepStore.tasks" :key="task.id">
                                        <td>{{ task.is_completed? 'Yes' : 'No' }}</td>
                                        <td>{{ task.name }}</td>
                                        <td>{{ task.description }}</td>
                                        <td class="d-table-cell align-start" style="max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" :title="task.url || ''" data-bs-toggle="tooltip">
                                            <a v-if="task.url" :href="task.url" target="_blank" class="text-decoration-none">
                                                {{ task.url }} <i class="fas fa-external-link-alt ms-1" style="font-size: 0.85em;"></i>
                                            </a>
                                            <span v-else class="text-muted">--</span>
                                        </td>
                                        <td>{{ task.due_at ? formatDateForDisplay(task.due_at) : '' }}</td>
                                        <td>{{ task.action }}</td>
                                        <td class="d-table-cell ">
                                            <button class="btn btn-primary" @click="editTask(task)"><i class="fas fa-edit"></i></button>
                                            <button class="btn btn-danger" @click="deleteTask(task)"><i class="fas fa-trash"></i></button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add / Edit Task Modal -->
        <div class="modal fade" id="taskModal" tabindex="-1" aria-labelledby="taskModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-lg">
                <div class="modal-content">
                    <div class="modal-header border-bottom-0 pb-0">
                        <div class="text-center w-100">
                            <button type="button" class="btn btn-white float-end close-modal" data-bs-dismiss="modal" data-bs-toggle="tooltip" title="Close">
                                <img class="icon" src="/img/icons/avid-icons/close.svg" alt=""> 
                            </button>
                            <h3 class="mt-2">{{ id ? 'Edit' : 'Add' }} Task</h3>
                        </div>
                    </div>
                    <div class="modal-body">
                        <form @submit.prevent="saveTask">
                            <div class="mb-3">
                                <label for="taskName" class="form-label">Name <span class="text-danger">*</span></label>
                                <input type="text" class="form-control" :class="{ 'is-invalid': errors.name }" id="taskName" v-model="name" @input="clearError('name')" aria-label="Name">
                                <div v-if="errors.name" class="invalid-feedback">{{ errors.name }}</div>
                            </div>
                            <div class="mb-3">
                                <label for="taskDescription" class="form-label">Description <span class="text-danger">*</span></label>
                                <textarea class="form-control" :class="{ 'is-invalid': errors.description }" id="taskDescription" v-model="description" rows="3" @input="clearError('description')"></textarea>
                                <div v-if="errors.description" class="invalid-feedback">{{ errors.description }}</div>
                            </div>
                            <div class="mb-3">
                                <label for="taskUrl" class="form-label">URL (optional)</label>
                                <input type="url" class="form-control" id="taskUrl" v-model="url" aria-label="URL (optional)">
                            </div>
                            <div class="mb-3">
                                <label for="taskDueAt" class="form-label">Due On <span class="text-danger">*</span></label>
                                <datepicker-utc 
                                    v-model="due_at" 
                                    :input-class="errors.due_at ? 'form-control is-invalid' : 'form-control'"
                                    placeholder="Select Date"
                                    input-format="M/d/yyyy"
                                    @update:modelValue="clearError('due_at')"
                                ></datepicker-utc>
                                <div v-if="errors.due_at" class="invalid-feedback d-block">{{ errors.due_at }}</div>
                            </div>
                            <div class="mb-3">
                                <label for="taskAction" class="form-label">Hubspot Field to update (optional)</label>
                                <input type="text" class="form-control" id="taskAction" v-model="action" aria-label="Hubspot Field to update (optional)">
                            </div>
                            <input type="hidden" id="taskId" v-model="id" />
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                        <button type="button" class="btn btn-primary" @click="saveTask" :disabled="form.saving">
                            <span v-if="form.saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
                            <span v-else><img class="icon me-1" src="/img/icons/avid-icons/save.svg" alt=""></span>
                            {{ form.saving ? 'Saving...' : 'Save Task' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import Tasks from '../lockstep/Tasks.vue';
import DatepickerUtc from '../components/DatepickerUtc.vue';
import { useLockstepStore } from '../../stores/lockstep';
import Swal from 'sweetalert2';
import { useNotifications } from '../../composables/useNotifications';

export default defineComponent({
    name: 'ManageClientTasks',
    components: {
        Tasks,
        DatepickerUtc
    },
    props: {
        client: {
            type: Object,
            required: true
        }
    },
    setup() {
        const { showNotification } = useNotifications();
        return {
            showNotification
        };
    },
    data() {
        return {
            lockstepStore: useLockstepStore(),
            name: '',
            description: '',
            url: '',
            due_at: null,
            action: '',
            id: null,
            client_id: this.client.id,
            form: {
                saving: false
            },
            errors: {
                name: '',
                description: '',
                due_at: ''
            }
        };
    },
    beforeMount() {
        this.lockstepStore.initialize(window.__LOCKSTEP__);
    },
    mounted() {
        this.lockstepStore.subscribeToEcho(window.Echo);
    },
    beforeUnmount() {
        this.lockstepStore.unsubscribeFromEcho(window.Echo);
    },
    methods: {
        formatDateForDisplay(dateValue) {
            if (!dateValue) return '';
            
            // Parse the date (handles both Date objects and ISO strings)
            const date = dateValue instanceof Date ? new Date(dateValue) : new Date(dateValue);
            
            // Adjust for timezone offset to preserve the date value (same as DatepickerUtc)
            // This ensures UTC dates like "2025-11-03T00:00:00.000000Z" display as 11/3/2025
            // regardless of the user's timezone
            const offset = date.getTimezoneOffset();
            date.setMinutes(date.getMinutes() + offset);
            
            // Format as M/d/yyyy to match DatepickerUtc's inputFormat (no leading zeros)
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const year = date.getFullYear();
            
            return `${month}/${day}/${year}`;
        },
        openTaskModal() {
            // Reset form
            this.name = '';
            this.description = '';
            this.url = '';
            this.due_at = null;
            this.action = '';
            this.id = null;
            this.errors = {
                name: '',
                description: '',
                due_at: ''
            };
            this.form.saving = false;
            
            // Show the modal
            const modal = new bootstrap.Modal(window.document.getElementById('taskModal'));
            modal.show();
        },
        validateForm() {
            let isValid = true;
            
            // Reset errors
            this.errors = {
                name: '',
                description: '',
                due_at: ''
            };
            
            // Validate name
            if (!this.name || this.name.trim() === '') {
                this.errors.name = 'Name is required';
                isValid = false;
            }
            
            // Validate description
            if (!this.description || this.description.trim() === '') {
                this.errors.description = 'Description is required';
                isValid = false;
            }
            
            // Validate due_at
            if (!this.due_at) {
                this.errors.due_at = 'Due date is required';
                isValid = false;
            }
            
            return isValid;
        },
        clearError(field) {
            if (this.errors[field]) {
                this.errors[field] = '';
            }
        },
        saveTask(event) {
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            
            // Validate form before submitting
            if (!this.validateForm()) {
                return;
            }
            
            this.form.saving = true;
            
            // Store whether this is an edit operation before making the API call
            const isEdit = !!this.id;
            
            // Format the date for the API (ISO string)
            const dueAtValue = this.due_at instanceof Date ? this.due_at.toISOString() : this.due_at;
            
            window.axios.post(`/api/task/${this.client.url}/save`, {
                name: this.name,
                description: this.description,
                url: this.url,
                due_at: dueAtValue,
                action: this.action,
                id: this.id,
                client_id: this.client_id
            }).then(response => {
                this.form.saving = false;
                
                // Close the modal immediately after API success
                const modalElement = window.document.getElementById('taskModal');
                if (modalElement) {
                    const modal = bootstrap.Modal.getInstance(modalElement);
                    if (modal) {
                        modal.hide();
                    }
                }
                
                // Reset form
                this.name = '';
                this.description = '';
                this.url = '';
                this.due_at = null;
                this.action = '';
                this.id = null;
                // Clear any errors
                this.errors = {
                    name: '',
                    description: '',
                    due_at: ''
                };
                // Update the tasks array
                this.lockstepStore.tasks = response.data.tasks;
                
                // Show success message
                const taskName = response.data.tasks.find(t => t.id === response.data.tasks[response.data.tasks.length - 1].id)?.name || this.name;
                this.showNotification({
                    text: isEdit ? '<strong>' + this.name + '</strong> was updated!' : '<strong>' + taskName + '</strong> was added!'
                });
            }).catch(error => {
                this.form.saving = false;
                console.error('Error saving task:', error);
                
                this.showNotification({
                    text: 'Failed to save task. ' + (error.response?.data?.message || 'Please try again.'),
                    type: 'error'
                });
            });
        },
        editTask(task) {
            this.name = task.name;
            this.description = task.description;
            this.url = task.url || '';
            
            // Convert the due_at string to a Date object for DatepickerUtc
            if (task.due_at) {
                // Parse the date string and create a Date object
                // The DatepickerUtc component will handle timezone adjustments
                const date = new Date(task.due_at);
                this.due_at = !isNaN(date.getTime()) ? date : null;
            } else {
                this.due_at = null;
            }
            
            this.action = task.action || '';
            this.id = task.id;
            // Clear any errors when editing
            this.errors = {
                name: '',
                description: '',
                due_at: ''
            };
            this.form.saving = false;
            
            // Show the modal
            const modal = new bootstrap.Modal(window.document.getElementById('taskModal'));
            modal.show();
        },
        deleteTask(task) {
            window.axios.delete(`/api/task/${this.client.url}/${task.id}`, {
                id: task.id
            }).then(response => {
                //Remove the task from the array
                this.lockstepStore.tasks = this.lockstepStore.tasks.filter(t => t.id !== task.id);
                
                // Show success message
                this.showNotification({
                    text: '<strong>' + task.name + '</strong> was deleted!'
                });
            }).catch(error => {
                console.error('Error deleting task:', error);
                
                this.showNotification({
                    text: 'Failed to delete <strong>' + task.name + '</strong>. Please try again.',
                    type: 'error'
                });
            });
        }
    }
});
</script>
